import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { X } from 'shared/icons';

const ToastContext = createContext();

function createToastId() {
    return `${Date.now()}-${Math.floor(Math.random() * 1_000_000)}`;
}

function normalizeActions(payload = {}) {
    if (Array.isArray(payload.actions) && payload.actions.length > 0) {
        return payload.actions
            .map((action) => ({
                label: String(action?.label || '').trim(),
                onClick: typeof action?.onClick === 'function' ? action.onClick : null,
                variant: action?.variant === 'secondary' ? 'secondary' : 'primary',
                closeOnClick: action?.closeOnClick !== false,
            }))
            .filter((action) => action.label.length > 0);
    }

    if (payload.actionLabel) {
        return [
            {
                label: String(payload.actionLabel).trim(),
                onClick: typeof payload.onAction === 'function' ? payload.onAction : null,
                variant: payload.actionVariant === 'secondary' ? 'secondary' : 'primary',
                closeOnClick: payload.closeOnAction !== false,
            },
        ].filter((action) => action.label.length > 0);
    }

    return [];
}

/**
 * Aceita também `Error`/`APIError` diretos — extrai `message` e `code`. Útil pra
 * `toast.error(err)` em catch — formata user-friendly e ainda exibe o código
 * estável catalogado em `core/errors/errorCodes.js` (backend) ou inline (frontend).
 */
function normalizeToastInput(input, options = {}) {
    let payload;
    if (input instanceof Error) {
        // Em APIError o `.message` é a versão amigável (vinda do errorHandler) e
        // `.code` é o slug catalogado. Em Error genérico só temos .message.
        payload = { title: input.message, code: input.code };
    } else if (typeof input === 'string') {
        payload = { title: input };
    } else {
        payload = { ...(input || {}) };
    }
    const merged = { ...payload, ...options };

    const title = String(merged.title || merged.message || '').trim();
    if (!title) {
        return null;
    }

    let description = String(merged.description || '').trim();
    const code = String(merged.code || '').trim();
    // Se não houver description explícita E houver code, o código vai como
    // description ("código: XXX") pra equipe identificar a origem na hora.
    if (!description && code) {
        description = `código: ${code}`;
    }
    const actions = normalizeActions(merged);

    const inferredVariant = actions.length > 0 ? 'interactive' : description ? 'description' : 'default';
    const variant = merged.variant || inferredVariant;
    const type = merged.type || 'info';
    const autoCloseMs =
        merged.durationMs === undefined ? (variant === 'interactive' ? 0 : 5000) : Number(merged.durationMs) || 0;

    return {
        id: createToastId(),
        type,
        variant,
        title,
        description,
        code,
        actions,
        dismissible: merged.dismissible !== false,
        autoCloseMs,
    };
}

function toastToneClasses(type) {
    if (type === 'success') {
        return {
            border: 'border-success-border dark:border-success-900',
            actionPrimary: 'text-success-600 hover:text-success-700 dark:text-success-400 dark:hover:text-success-300',
        };
    }

    if (type === 'error') {
        return {
            border: 'border-danger-border dark:border-danger-900',
            actionPrimary: 'text-danger-600 hover:text-danger-700 dark:text-danger-400 dark:hover:text-danger-300',
        };
    }

    return {
        border: 'border-mono-200 dark:border-mono-800',
        actionPrimary: 'text-link-600 hover:text-link-700 dark:text-link-400 dark:hover:text-link-300',
    };
}

// Card segue o padrão de superfície do DS (Card/Modal): bg-white no claro,
// mono-950 no escuro; borda carrega o tom semântico (success/danger/link).
const TOAST_CARD_CLASSES = 'mb-4 flex w-full max-w-sm gap-4 rounded-xl border bg-white px-6 py-5 shadow-sm dark:bg-mono-950 dark:shadow-lg-mono';
const TOAST_DISMISS_CLASSES =
    'inline-flex flex-shrink-0 items-center justify-center text-mono-400 transition-all duration-150 hover:text-mono-600 dark:text-mono-500 dark:hover:text-mono-300 pointer-coarse:min-h-11 pointer-coarse:min-w-11';
const TOAST_ACTION_SECONDARY_CLASSES =
    'text-black hover:text-mono-700 dark:text-white dark:hover:text-mono-300';

function ToastCard({ toast, onRemove }) {
    const tone = toastToneClasses(toast.type);
    // Erros são anunciados de forma assertiva por leitores de tela.
    const alertRole = toast.type === 'error' ? 'alert' : undefined;

    const handleActionClick = (action) => {
        if (typeof action.onClick === 'function') {
            action.onClick();
        }

        if (action.closeOnClick) {
            onRemove(toast.id);
        }
    };

    if (toast.variant === 'default') {
        const primaryAction = toast.actions[0] || null;

        return (
            <div role={alertRole} className={`${TOAST_CARD_CLASSES} ${tone.border}`}>
                <p className="text-base font-medium text-black dark:text-white">{toast.title}</p>
                <div className="ml-auto flex items-center space-x-3">
                    {primaryAction ? (
                        <button
                            type="button"
                            className={`inline-flex items-center justify-center gap-2 text-sm font-semibold hover:underline ${primaryAction.variant === 'secondary' ? TOAST_ACTION_SECONDARY_CLASSES : tone.actionPrimary}`}
                            onClick={() => handleActionClick(primaryAction)}
                        >
                            {primaryAction.label}
                        </button>
                    ) : null}
                    {toast.dismissible ? (
                        <button
                            type="button"
                            aria-label="Fechar toast"
                            className={TOAST_DISMISS_CLASSES}
                            onClick={() => onRemove(toast.id)}
                        >
                            <X size={20} />
                        </button>
                    ) : null}
                </div>
            </div>
        );
    }

    return (
        <div role={alertRole} className={`${TOAST_CARD_CLASSES} ${tone.border}`}>
            <div className="block">
                <h5 className="mb-1 text-sm font-medium text-black dark:text-white">{toast.title}</h5>
                {toast.description ? (
                    <p className="text-sm font-medium text-mono-600 dark:text-mono-400">{toast.description}</p>
                ) : null}
                {toast.variant === 'interactive' && toast.actions.length > 0 ? (
                    <div className="mt-4 inline-flex space-x-3">
                        {toast.actions.map((action) => (
                            <button
                                key={`${toast.id}-${action.label}`}
                                type="button"
                                className={`text-sm font-semibold ${
                                    action.variant === 'secondary'
                                        ? TOAST_ACTION_SECONDARY_CLASSES
                                        : tone.actionPrimary
                                }`}
                                onClick={() => handleActionClick(action)}
                            >
                                {action.label}
                            </button>
                        ))}
                    </div>
                ) : null}
            </div>
            {toast.dismissible ? (
                <button
                    type="button"
                    aria-label="Fechar toast"
                    className={TOAST_DISMISS_CLASSES}
                    onClick={() => onRemove(toast.id)}
                >
                    <X size={20} />
                </button>
            ) : null}
        </div>
    );
}

export const useToast = () => {
    const context = useContext(ToastContext);
    if (!context) {
        throw new Error('useToast must be used within ToastProvider');
    }
    return context;
};

export const ToastProvider = ({ children }) => {
    const [toasts, setToasts] = useState([]);
    const timeoutMapRef = useRef(new Map());

    const removeToast = useCallback((id) => {
        const timeoutId = timeoutMapRef.current.get(id);
        if (timeoutId) {
            clearTimeout(timeoutId);
            timeoutMapRef.current.delete(id);
        }
        setToasts((prev) => prev.filter((toast) => toast.id !== id));
    }, []);

    useEffect(() => {
        const timeoutMap = timeoutMapRef.current;
        return () => {
            timeoutMap.forEach((timeoutId) => {
                clearTimeout(timeoutId);
            });
            timeoutMap.clear();
        };
    }, []);

    const show = useCallback(
        (input, options = {}) => {
            const nextToast = normalizeToastInput(input, options);
            if (!nextToast) {
                return null;
            }

            setToasts((prev) => [...prev, nextToast]);

            if (nextToast.autoCloseMs > 0) {
                const timeoutId = setTimeout(() => {
                    removeToast(nextToast.id);
                }, nextToast.autoCloseMs);
                timeoutMapRef.current.set(nextToast.id, timeoutId);
            }

            return nextToast.id;
        },
        [removeToast],
    );

    const success = useCallback((input, options = {}) => show(input, { ...options, type: 'success' }), [show]);
    const error = useCallback((input, options = {}) => show(input, { ...options, type: 'error' }), [show]);
    const info = useCallback((input, options = {}) => show(input, { ...options, type: 'info' }), [show]);
    const defaultToast = useCallback(
        (message, options = {}) => show(message, { ...options, variant: 'default' }),
        [show],
    );
    const withDescription = useCallback(
        (title, description, options = {}) =>
            show(
                {
                    title,
                    description,
                    variant: 'description',
                },
                options,
            ),
        [show],
    );
    const interactive = useCallback(
        (payload, options = {}) => show({ ...(payload || {}), variant: 'interactive' }, options),
        [show],
    );

    const value = useMemo(
        () => ({
            show,
            success,
            error,
            info,
            defaultToast,
            withDescription,
            interactive,
            remove: removeToast,
        }),
        [show, success, error, info, defaultToast, withDescription, interactive, removeToast],
    );

    return (
        <ToastContext.Provider value={value}>
            {children}
            {/* Região viva: leitores de tela anunciam toasts novos sem roubar o
                foco (polite); erros individuais sobem como role="alert". */}
            <div className="fixed right-4 top-4 z-50" role="status" aria-live="polite" aria-atomic="true">
                {toasts.map((toast) => (
                    <ToastCard key={toast.id} toast={toast} onRemove={removeToast} />
                ))}
            </div>
        </ToastContext.Provider>
    );
};

export default ToastProvider;
